/**
 * The helplines, on every page a student sees (Supreme Court Sukdeb Saha
 * guidelines: helplines "prominently displayed"; UMMEED: counsellors' numbers
 * displayed), plus the private "How are you today?" check-in when the school
 * allows it. The note goes ONLY to the school's named counsellors.
 */
import { useState } from "react";
import { extractErrorMessage } from "../../services/errorReporter";
import { logEvent } from "../../services/logger";
import { type Helpline, MOODS, useCheckInMutation, useGetMyWellbeingQuery, useGetPublicHelplinesQuery } from "./wellbeingApi";
import "./wellbeing.css";

export const HelplineList = ({ helplines }: { helplines: Helpline[] }) => (
    <ul className="helplines__list">
        {helplines.map((h) => (
            <li key={`${h.label}-${h.number}`}>
                <strong>{h.label}</strong>{" "}
                <a href={`tel:${h.number.replace(/[^0-9+]/g, "")}`}>{h.number}</a>
                {h.hours ? <span className="helplines__hours"> · {h.hours}</span> : null}
            </li>
        ))}
    </ul>
);

const CheckinDialog = ({ onClose }: { onClose: () => void }) => {
    const [mood, setMood] = useState<number | null>(null);
    const [note, setNote] = useState("");
    const [checkIn, { isLoading }] = useCheckInMutation();
    const [done, setDone] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const send = async () => {
        if (mood === null) return;
        try {
            await checkIn({ mood, note }).unwrap();
            setDone(true);
            logEvent({ level: "INFO", component: "HelplineBanner", message: "wellbeing.checkin_sent" });
        } catch (err) {
            setProblem(extractErrorMessage(err));
        }
    };

    return (
        <div className="checkin" role="dialog" aria-label="How are you today?">
            {done ? (
                <>
                    <p>Thank you for telling us. Only your school's counsellor will read this.</p>
                    <button type="button" className="btn btn-ghost btn-compact" onClick={onClose}>Close</button>
                </>
            ) : (
                <>
                    <p className="form-label">How are you today?</p>
                    <div className="checkin__moods" role="radiogroup" aria-label="Your mood">
                        {MOODS.map((m) => (
                            <button
                                key={m.value}
                                type="button"
                                role="radio"
                                aria-checked={mood === m.value}
                                aria-label={m.label}
                                className={`checkin__mood${mood === m.value ? " is-on" : ""}`}
                                onClick={() => setMood(m.value)}
                            >
                                <span aria-hidden="true">{m.face}</span>
                            </button>
                        ))}
                    </div>
                    <label className="form-field">
                        <span className="form-label">Anything you want your counsellor to know? (optional)</span>
                        <textarea className="text-input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
                    </label>
                    <p className="form-hint">Only your school's counsellor sees this. Not your teachers, not your classmates.</p>
                    {problem ? <p className="form-hint course-flag" role="alert">{problem}</p> : null}
                    <div className="row-actions">
                        <button type="button" className="btn btn-primary" disabled={mood === null || isLoading} onClick={send}>Send to my counsellor</button>
                        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                    </div>
                </>
            )}
        </div>
    );
};

/** Signed out, on an organisation's login page: its helplines, if it set any. */
export const LoginHelplines = ({ slug }: { slug: string }) => {
    const { data: helplines = [] } = useGetPublicHelplinesQuery(slug);
    if (!helplines.length) return null;
    return (
        <aside className="helplines" aria-label="Need to talk?">
            <span className="helplines__title">Need to talk? You are not alone.</span>
            <HelplineList helplines={helplines} />
        </aside>
    );
};

export const HelplineBanner = () => {
    const { data } = useGetMyWellbeingQuery();
    const [open, setOpen] = useState(false);
    if (!data || (!data.helplines.length && !data.can_check_in)) return null;
    return (
        <aside className="helplines" aria-label="Need to talk?">
            <div className="helplines__row">
                <span className="helplines__title">Need to talk? You are not alone.</span>
                {data.helplines.length ? <HelplineList helplines={data.helplines} /> : null}
                {data.can_check_in && !open ? (
                    <button type="button" className="btn btn-ghost btn-compact" onClick={() => setOpen(true)}>How are you today?</button>
                ) : null}
            </div>
            {open ? <CheckinDialog onClose={() => setOpen(false)} /> : null}
        </aside>
    );
};
